import WebGL from 'three/addons/capabilities/WebGL.js'
import gsap from 'gsap'
import { Transform, Camera } from 'ogl'

import Renderer from './Renderer'

import World from './World/World'

import Time from './Utils/Time'
import Sizes from './Utils/Sizes'
import Debug from './Utils/Debug'
import Mouse from './Utils/Mouse'

import Assets from './Assets/Assets'

import State from '@/lib/hey'

let instance = null

export default class Gl {
  constructor(_params) {
    /* 
      Singleton
    */
    if (instance) {
      return instance
    }

    instance = this

    /* 
      Check if WebGL 2.0 is available
    */
    if (WebGL.isWebGL2Available()) {
      this.setup(_params)
    } else {
      // Fallback
      console.log('[WebGL] [   (╯︵╰,)   ] -', 'WebGL 2.0 is not available - initializing fallback.')

      document.documentElement.classList.add('webgl-not-available')
    }
  }

  setup(_params) {
    /* 
      Get Debug 
    */
    this.urlParams = new URLSearchParams(window.location.search)
    this.isDebug = this.urlParams.has('debug')

    /* 
      Params
    */
    this.params = _params

    /* 
      Flags
    */
    this.isLoaded = false

    /* 
      Canvas
    */
    this.canvas = document.querySelector(this.params.canvas)

    /* 
      Utils
    */
    this.time = new Time()
    this.sizes = new Sizes()
    this.mouse = new Mouse(this.canvas, {
      limitToBounds: true,
    })

    /* 
      Renderer
    */
    this.renderer = new Renderer()

    /* 
      Scene & Camera
    */
    this.scene = new Transform()
    this.camera = new Camera(this.renderer.instance.gl)

    /* 
      Assets
    */
    this.assets = new Assets()

    /* 
      State
    */
    this.state = {
      data: null,
      canvasContainer: null,
    }

    // Store default data before loaded
    State.on('WEBGL_DATA', (data) => {
      if (this.isLoaded) return

      this.state.data = data
    })

    // Handle mouse update
    State.on('CANVAS_CONTAINER', (data) => {
      const isSticky = document.querySelector('[data-gl="canvas"]').closest('.home-hero_sticky')

      this.state.canvasContainer = isSticky ? null : document.querySelector('[data-gl="canvas"]').parentElement

      this.mouse.dom = isSticky ? null : document.querySelector('[data-gl="canvas"]').parentElement

      this.resize()
    })

    /* 
      Functions
    */
    window.addEventListener('resize', () => {
      this.resize()
    })
  }

  load() {
    return new Promise((_resolve) => {
      if (WebGL.isWebGL2Available()) {
        Promise.all([this.loadDOM(), this.assets.load()]).then(() => {
          this.init()

          this.isLoaded = true

          _resolve()
        })
      } else {
        // Fallback
        _resolve()
      }
    })
  }

  init() {
    if (this.isDebug) this.debug = new Debug()

    this.world = new World()
    gsap.ticker.add(this.update.bind(this))

    this.world.add()

    this.mouse.updateBounds()

    if (this.isDebug) {
      this.world.debugSceneParamsFolder
        .addBinding(this.sizes, 'pixelRatio', {
          label: 'Pixel Ratio',
          min: 0.1,
          max: 4,
          step: 0.1,
        })
        .on('change', () => {
          this.sizes.isPixelRatioOverride = true
          this.resize()
        })
    }

    console.log('[WebGL] [ █ █ █ █ █ █ ] -', 'Initialized')
  }

  loadDOM() {
    return new Promise((_resolve) => {
      if (document.readyState === 'interactive') {
        console.log('[WebGL] [ █ █         ] -', 'DOM loaded')
        _resolve()
      } else {
        document.addEventListener('DOMContentLoaded', () => {
          console.log('[WebGL] [ █ █         ] -', 'DOM loaded')
          _resolve()
        })
      }
    })
  }

  update() {
    if (this.isLoaded) {
      if (this.isDebug) this.debug.stats.begin()

      this.time.update()
      this.world.update()
      this.renderer.update()
      this.mouse.update()

      if (this.isDebug) {
        this.debug.stats.end()
        this.debug.stats.update()
      }
    }
  }

  resize() {
    this.sizes.resize(this.state.canvasContainer)

    if (this.isLoaded) {
      this.renderer.resize()
      this.world.resize()
    }

    this.mouse.resize()
  }
}
